import React, { useRef, useEffect, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import actions from "../../actions";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import ErrorMessage from "../custom/containers/ErrorMessage";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ERPSTransactionDetails from "../custom/ERPSTransactionDetails";
import { Regex, FI }  from "../../constants/sharedConstants.js";
import { listProps, checkFieldValue, checkLengthAndRegex } from "../../utils/helper";
import { Alert, Dialog, DialogActions, DialogContent, HStack, ResponsiveBox, Stack, Typography, VStack } from "../primitives";

const RecallPaymentCustomerDetails = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        location: { state },
    } = props;

    const user = useSelector((state) => state.loginUserReducer.user);

    const selectedFiNumber = useSelector((state) => state.fiReducer?.selectedFi);

    const rpcData = useSelector(
        (state) => state.recallPaymentCustomerReducer?.rpcData?.data
    );

    const successMessage = useSelector(
        (state) => state.transactionEnquiryReducer?.recallTransactionData
    );

    const [errorMessage, setErrorMessage] = useState(null);
    const recallErrorMessage = useSelector(
        (state) => state.transactionEnquiryReducer?.recallTransactionError
    );

    const fi_number = selectedFiNumber ? selectedFiNumber : user?.fiNumber;

    const errorRef = useRef();
    const successRef = useRef();
    const printComponentRef = useRef();
    const warningRef = useRef();

    let data = rpcData ? rpcData.find(({ payment_id }) => payment_id === state.id) : {};
    const isShowFiTrackingNumber = (data.fi_number === FI.CIBC);  // only when transaction's fi_number is 10

    const [showDialog, setShowDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [recallReason, setRecallReason] = useState('');
    const [fiTrackingNumber, setFiTrackingNumber] = useState('');
    const [isRecalled, setIsRecalled] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [recallReasonError, setRecallReasonError] = useState("");
    const [fiTrackingNumberError, setFiTrackingNumberError] = useState("");
    const [hasShowWarningMessage, setShowWarningMessage] = useState(false);
    const [warningShown, setWarningShown] = useState(false);

    useEffect(() => {
        if (showSuccessMessage)
            setIsRecalled(true);
        if (successRef.current) {
            successRef.current.focus();
        }
    }, [showSuccessMessage]);

    useEffect(() => {
        if (warningRef.current) {
            warningRef.current.focus();
        }
    }, [hasShowWarningMessage]);

    useEffect(() => {
        setErrorMessage(recallErrorMessage);
        if (errorRef.current) {
            errorRef.current.focus();
        }
    }, [recallErrorMessage]);

    useEffect(() => {
        dispatch({ type: "UPDATE_TRANSACTION_FOR_RECALL_SUCCESS", data: null })

        return () => onExit;  // clean up the ref on exit
    }, []);

    const onExit = () => {
        errorRef.current = null;
        successRef.current = null;
        printComponentRef.current = null;
    }

    const handleChange = (e) => {
        const value = e.target.value;
        if (e.target.id === "recallReason") {
            let errMsg = checkLengthAndRegex(value, "erps.transactionEnquiry.label.recall.reason", 80, Regex.regularText);
            // one invalid character will show in the field, but further input isn't allowed until the invalid character is removed
            setRecallReasonError(errMsg);
            if ("" === errMsg || Regex.regularText.test(recallReason))  // if either current value is valid or previous value is valid
                setRecallReason(value);
        } else if (isShowFiTrackingNumber && e.target.id === "fiTrackingNumber") {
            let errMsg = checkLengthAndRegex(value, "erps.transactionEnquiry.label.trackingNumber", 15, Regex.alphaNumeric);
            // one invalid character will show in the field, but further input isn't allowed until the invalid character is removed
            setFiTrackingNumberError(errMsg);
            if ("" === errMsg || Regex.alphaNumeric.test(fiTrackingNumber))  // if either current value is valid or previous value is valid
                setFiTrackingNumber(value);
        }
    };

    const handleConfirmRecall = () => {
        if (validateInput()) {
            if (!validateAgreementAccepted() && !warningShown) {
                setShowWarningMessage(true);
                setWarningShown(true);
                return;
            }
            if (hasShowWarningMessage) {
                setShowWarningMessage(false);
            }
            setErrorMessage(null);
            setShowDialog(true);
        }
    };

    const validateInput = () => {
        let errorMsgReason = checkFieldValue(recallReason, "erps.transactionEnquiry.label.recall.reason", true, (v) => Regex.regularText.test(v));
        setRecallReasonError(errorMsgReason);
        if (!isShowFiTrackingNumber)
            return "" === errorMsgReason;

        let errorMsgTrackingNum = checkFieldValue(fiTrackingNumber, "erps.transactionEnquiry.label.trackingNumber", true, (v) => Regex.alphaNumeric.test(v));
        setFiTrackingNumberError(errorMsgTrackingNum);
        return "" === errorMsgReason && "" === errorMsgTrackingNum;
    }

    const validateAgreementAccepted = () => {
       return data?.pad_accepted_flag === 'Y' || data?.pad_accepted_flag === 'X';
    }

    const handleRecall = async  () => {
        setShowDialog(false);
        setLoading(true);
        if (showSuccessMessage) {
            setShowSuccessMessage(false);
        }
        let reqBodyForRecall = {};
        if (data) {
            const { id, payment_date } = data;
            reqBodyForRecall = {
                correlation_id: id,
                fi_number,
                payment_date,
                recall_reject_reason: recallReason,
                fi_tracking_number: fiTrackingNumber,
                recalled_rejected_by: user?.userId,
                language_code: user?.languageCode
            };
        }
        dispatch(
            actions.transactionEnquiryActions.recallTransaction(
                {  reqBodyForRecall },
                { setShowSuccessMessage, setLoading }
            )
        );
    };

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current
    });
    const detailArguments = successMessage ? {data: data, recallReason: recallReason, recalledBy: user?.userId} : {data: data};

    return(
        <div role="main">
            {successMessage && showSuccessMessage && (
                <ConfirmationMessage
                    message={successMessage}
                    setShowMessage={setShowSuccessMessage}
                    inputRef={successRef}
                />
            )}
            <ErrorMessage 
              error={errorMessage}
              errorRef={errorRef}
              mt={2}
            />
            {hasShowWarningMessage && (
                <VStack
                    sx={{ width: "100%" }}
                    spacing={2}
                    tabIndex={0}
                    ref={warningRef}
                >
                    <Alert severity="warning">
                        {t("erps.transactionEnquiry.label.warningMessage")}
                    </Alert>
                </VStack>
            )}
            <ResponsiveBox st={{ ml: 3}}>
                <Breadcrumb {...props} fromDetailPage={true}/>
            </ResponsiveBox>

            <ERPSTransactionDetails {...detailArguments} ref={printComponentRef}/>

            {!successMessage && (
                <ResponsiveBox id={"screenshot-element"} sx={{ ml: 3 }}>
                    <ResponsiveBox sx={{ mt: 0, display: "flex" }}>
                        <Typography variant="label" className="transaction__enquiry__value">
                            <ERPSBaseTextField
                                id="recallReason"
                                type="text"
                                label={t("erps.transactionEnquiry.label.recall.reason")}
                                className="transactionEnquiry__textField__width"
                                size="small"
                                onChange={handleChange}
                                value={recallReason}
                                required="true"
                                inputProps={{ maxLength: 80 }}
                                error={!!recallReasonError}
                                helperText={recallReasonError}
                            />
                        </Typography>
                        <Typography variant="label" className="transaction__enquiry__value left_space">
                            {isShowFiTrackingNumber && (
                                <ERPSBaseTextField
                                     style={{border: "1px solid blue"}}
                                    id="fiTrackingNumber"
                                    type="text"
                                    label={t("erps.transactionEnquiry.label.trackingNumber")}
                                    className="transactionEnquiry__textField__width"
                                    size="small"
                                    onChange={handleChange}
                                    value={fiTrackingNumber}
                                    required="true"
                                    inputProps={{ maxLength: 15 }}
                                    error={!!fiTrackingNumberError}
                                    helperText={fiTrackingNumberError}
                                />
                            )}
                        </Typography>
                        <Typography className="transaction__enquiry__value left_space">{""}</Typography>
                    </ResponsiveBox>
                </ResponsiveBox>
            )}

            <HStack spacing={2} ml={3}>
                <ERPSBaseButton
                    variant="secondary"
                    className="button_with_margin48"
                    label={t("erps.newuser.button.back", "Back")}
                    onClick={() =>
                        props.history.push("/recall-payments-customers", {
                            fromDetailPage: true,
                            isRecalled: isRecalled
                        })
                    }
                />
                {!successMessage ? (
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.recallpaymentcustomers.page.label.confirm.recall")}
                        onClick={handleConfirmRecall}
                        loading={loading}
                        isDisabled={isRecalled}
                />) : (
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.transactionEnquiry.page.transactionDetails.button.print")}
                        onClick={handlePrint}
                        loading={loading}
                    />
                )}

            </HStack>
            <Dialog
                open={showDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
            >
                <DialogHeader
                    id="alert-dialog-title"
                    onClose={() => {
                        setShowDialog(false)
                        setWarningShown(false)}}
                    className={"box-dialog-header"}
                >
                    {t("erps.common.label.confirmation")}
                </DialogHeader>
                <DialogContent
                    id="alert-dialog-description"
                    className={"dialog-content-size"}
                >
                    {t("erps.recallpaymentcustomers.confirm.recall.transaction")}
                </DialogContent>
                <DialogActions className={"box-dialog-buttons"}>
                    <ERPSBaseButton
                        variant="secondary"
                        className="button_with_zeromargin"
                        onClick={() => { setShowDialog(false)
                            setWarningShown(false)}}
                        label={t("erps.common.button.cancel")}
                    />
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_zeromargin"
                        onClick={handleRecall}
                        label={t("erps.welcome.page.button.Ok")}
                    />
                </DialogActions>
            </Dialog>
        </div>

    );

};

export default withSidebar(RecallPaymentCustomerDetails);